<template>
  <SModal
    modal-id="file-viewer"
    max-content-width="100vw"
    class="fv-modal"
  >
    <template #default="{reject}">
      <div class="file-viewer file-viewer_wrapper">
        <div class="file-viewer_header">
          <div class="title">{{ label }}</div>
          <button class="file-viewer_btn-close" @click="reject('closed-by-cross')">
            <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 12.6216C19.1379 12.6217 19.25 12.7336 19.25 12.8716C19.25 12.9946 19.1602 13.0964 19.043 13.1167L18.9814 13.1216H4.00977C3.8717 13.1216 3.75977 13.0096 3.75977 12.8716C3.75977 12.7487 3.84886 12.646 3.96582 12.6255L4.02832 12.6216H19Z" fill="#1C1C1C" stroke="#1C1C1C"/>
              <path d="M9.83594 6.70264C9.93352 6.60504 10.0918 6.60514 10.1895 6.70264C10.2752 6.78836 10.2852 6.9211 10.2197 7.01807L10.1816 7.06299L4.70801 12.5376L4.35449 12.8911L4.70801 13.2446L10.1895 18.7271C10.2768 18.8145 10.2853 18.9501 10.2158 19.0474L10.1826 19.0854C10.0967 19.1655 9.96853 19.1746 9.87402 19.1108L9.81641 19.061L3.8252 13.0679C3.7386 12.9811 3.72813 12.8456 3.7959 12.7485L3.83691 12.7017L9.83594 6.70264Z" fill="#1C1C1C" stroke="#1C1C1C"/>
            </svg>
          </button>
        </div>
        <div class="file-viewer_info"></div>
        <div class="file-viewer_pdf-container">
          <PDFViewer
            :files="files"
            :image-formats="imageFormats"
            :file-index="fileIndex"
            :removable="removable"
            :downloadable="downloadable"
            @remove-file="onRemoveFile"
            @file-index-changed="onFileIndexChange"
          />
        </div>
      </div>
    </template>
  </SModal>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import SModal from '@components/modal/SModal.vue'
import { useModal } from '@components/modal/useModal'
import PDFViewer from '@components/PDFViewer/PDFViewer.vue'
import type { UploaderFile } from '@/types/uploader'

defineProps({
  files: {
    type: Array<UploaderFile>,
    required: true
  },
  fileIndex: {
    type: Number,
    default: 0
  },
  imageFormats: {
    type: Array<string>,
    default: () => ['apng', 'gif', 'ico', 'cur', 'jpg', 'jpeg', 'pjpeg', 'pjp', 'png', 'svg', 'webp', 'bmp']
  },
  label: {
    type: String,
    default: 'Просмотр файла'
  },
  removable: {
    type: Boolean,
    default: true
  },
  downloadable: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits<{
  'hide-dialog': []
  'remove-file': [index: number]
  'file-index-changed': [index: number]
}>()

const { openModal } = useModal()

const onRemoveFile = (index: number) => {
  emit('remove-file', index)
}

const onFileIndexChange = (index: number) => {
  emit('file-index-changed', index)
}

onMounted(async () => {
  try {
    if ('lock' in screen.orientation && typeof screen.orientation.lock === 'function') {
      await screen.orientation.lock('portrait')
    }
  } catch (error) {
    console.warn(error)
  }

  try {
    await openModal('file-viewer', {})
  } catch (err) {
    if (!(typeof err === 'string' && ['closed', 'closed-by-cross'].includes(err))) console.warn(err)
  } finally {
    emit('hide-dialog')
  }
})
</script>

<style lang="scss">

@mixin phone {
  @media screen and (max-width: 743px) {
    @content;
  }
}

@mixin narrow_tablet {
  @media screen and (max-width: 1199px) {
    @content;
  }
}

.fv-modal {
  @include narrow_tablet {
    top: 56px;
  }
  .vfm__content {
    width: 100%;
    height: 100%;
    max-height: calc(100vh - 48px);
    padding: 0 24px;
    display: block;
    @include narrow_tablet {
      padding: 0;
      .vfm-modal {
        border-radius: 0;
      }
    }
  }

  .vfm-modal {
    padding: 0 !important;
    overflow: hidden;
    height: 100%;
    width: 100%;
    max-width: unset;

    &__btn-close {
      top: 20px !important;
      right: 16px !important;
      @include narrow_tablet {
        display: none;
      }
    }
  }
}
</style>

<style lang="scss" scoped>
@import '@/assets/styles/scss/mixins.scss';
.file-viewer {
  :deep(.splide__list) {
    width: auto !important;
  }

  &_wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
  }

  &_header {
    display: flex;
    justify-content: space-between;
    font-size: 24px;
    line-height: 32px;
    font-weight: 500;
    max-width: 100%;
    padding: 16px 16px 0;
    margin-bottom: 16px;

    @include narrow_tablet {
      font-size: 20px;
      padding: 20px 20px 0;
      justify-content: center;
      margin-bottom: 12px;
      position: relative;
    }
    @include phone {
      padding: 16px 16px 0;
    }
  }
  &_btn-close {
    display: none;
    @include narrow_tablet {
      display: block;
      @include button-reset;
      position: absolute;
      left: 32px;
      top: 20px;
    }
  }

  &_footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 54px;
    background-color: var(--color-lbutton-light);
    font-size: 16px;
    padding-left: 40px;
    padding-right: 40px;
    font-weight: 400;
    max-width: 100%;
    color: var(--color-bg-primary);
  }

  &_pdf-container {
    display: flex;
    flex: 1 1 100%;
    overflow: hidden;
  }

  @media (max-width: 743px) {
    max-height: 100%;
    border-radius: 0;
  }
}
</style>
